const KEY_ALGO = {
  name: "RSA-OAEP",
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: "SHA-256",
};

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function storageKey(kind, userId) {
  return `fml_e2ee_${kind}_${String(userId || "")}`;
}

async function generateRsaKeyPair() {
  const keyPair = await window.crypto.subtle.generateKey(KEY_ALGO, true, ["encrypt", "decrypt"]);
  const spki = await window.crypto.subtle.exportKey("spki", keyPair.publicKey);
  const pkcs8 = await window.crypto.subtle.exportKey("pkcs8", keyPair.privateKey);

  return {
    publicKey: toBase64(spki),
    privateKey: toBase64(pkcs8),
  };
}

async function safeUploadPublicKey(uploadPublicKeyFn, publicKey) {
  if (typeof uploadPublicKeyFn !== "function") return;
  try {
    await uploadPublicKeyFn(publicKey);
  } catch {
    // Non-blocking: chat can continue with locally persisted keys.
  }
}

export async function ensureUserKeyPair(userId, serverPublicKey, uploadPublicKeyFn) {
  const privateKeyKey = storageKey("private", userId);
  const publicKeyKey = storageKey("public", userId);

  let privateKey = localStorage.getItem(privateKeyKey);
  let publicKey = localStorage.getItem(publicKeyKey) || serverPublicKey || "";

  if (!privateKey) {
    const generated = await generateRsaKeyPair();
    privateKey = generated.privateKey;
    publicKey = generated.publicKey;

    localStorage.setItem(privateKeyKey, privateKey);
    localStorage.setItem(publicKeyKey, publicKey);

    await safeUploadPublicKey(uploadPublicKeyFn, publicKey);
  } else if (!localStorage.getItem(publicKeyKey) && serverPublicKey) {
    localStorage.setItem(publicKeyKey, serverPublicKey);
    publicKey = serverPublicKey;
  } else if (privateKey && publicKey && !serverPublicKey) {
    await safeUploadPublicKey(uploadPublicKeyFn, publicKey);
  }

  return { privateKey, publicKey };
}

async function importPublicKey(base64PublicKey) {
  return window.crypto.subtle.importKey(
    "spki",
    fromBase64(base64PublicKey),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
}

async function importPrivateKey(base64PrivateKey) {
  return window.crypto.subtle.importKey(
    "pkcs8",
    fromBase64(base64PrivateKey),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["decrypt"]
  );
}

async function importAesKey(rawBase64, usages) {
  return window.crypto.subtle.importKey(
    "raw",
    fromBase64(rawBase64),
    { name: "AES-GCM", length: 256 },
    false,
    usages
  );
}

export async function createSessionKey() {
  const key = await window.crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const raw = await window.crypto.subtle.exportKey("raw", key);
  return toBase64(raw);
}

export async function encryptSessionKeyForPublicKey(sessionKeyBase64, receiverPublicKeyBase64) {
  const receiverPublicKey = await importPublicKey(receiverPublicKeyBase64);
  const encrypted = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, receiverPublicKey, fromBase64(sessionKeyBase64));
  return toBase64(encrypted);
}

export async function decryptSessionKey(encryptedSessionKeyBase64, privateKeyBase64) {
  const privateKey = await importPrivateKey(privateKeyBase64);
  const raw = await window.crypto.subtle.decrypt({ name: "RSA-OAEP" }, privateKey, fromBase64(encryptedSessionKeyBase64));
  return toBase64(raw);
}

export async function encryptTextWithSessionKey(plainText, sessionKeyBase64) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await importAesKey(sessionKeyBase64, ["encrypt"]);
  const encrypted = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    TEXT_ENCODER.encode(String(plainText || ""))
  );

  return {
    encryptedMessage: toBase64(encrypted),
    iv: toBase64(iv.buffer),
  };
}

export async function decryptTextWithSessionKey(encryptedMessageBase64, ivBase64, sessionKeyBase64) {
  const key = await importAesKey(sessionKeyBase64, ["decrypt"]);
  const decrypted = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(fromBase64(ivBase64)) },
    key,
    fromBase64(encryptedMessageBase64)
  );
  return TEXT_DECODER.decode(decrypted);
}

export async function encryptFileWithSessionKey(file, sessionKeyBase64) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await importAesKey(sessionKeyBase64, ["encrypt"]);
  const fileBuffer = await file.arrayBuffer();
  const encrypted = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, fileBuffer);

  return {
    encryptedFile: new Blob([encrypted], { type: "application/octet-stream" }),
    fileIv: toBase64(iv.buffer),
  };
}

export async function decryptFileWithSessionKey(encryptedArrayBuffer, ivBase64, sessionKeyBase64) {
  const key = await importAesKey(sessionKeyBase64, ["decrypt"]);
  const decrypted = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(fromBase64(ivBase64)) },
    key,
    encryptedArrayBuffer
  );
  return decrypted;
}
